import moment from 'moment'

//
// Helpers for send-on-change sensors (CS-* v5 firmware).
//
// These sensors keep measuring every minute but only transmit when a value
// changes by more than the configured threshold. A forced report arrives at
// the latest after maxSilence minutes (default 60) and carries a value tuple
// with systempart 4 / type 36 (CDC_NOCHANGE_COUNT) = the number of suppressed
// (unchanged) measurement minutes since the previous transmission. Semantics:
// the PREVIOUSLY sent value was valid for the whole silent window.
//
export const NOCHANGE_VALUETYPE = 36
export const DEFAULT_MAX_SILENCE_MINUTES = 60

//
// How far sensortime may lag behind recordedtime and still be trusted.
// Mirrors CDCData.PlausibleSensortimeWindowMs in the backend.
//
export const PLAUSIBLE_SENSORTIME_WINDOW_MS = 30 * 24 * 60 * 60 * 1000

//
// How far sensortime may run AHEAD of recordedtime (clock skew only — a
// measurement cannot postdate its ingestion). Mirrors
// CDCData.PlausibleSensortimeFutureSlackMs.
//
export const PLAUSIBLE_SENSORTIME_FUTURE_SLACK_MS = 10 * 60 * 1000

export default {
  //
  // The point in time a measurement REFERS TO — what a chart has to plot on.
  // Mirror of CDCData.getEffectiveTime() in the backend.
  //
  // recordedtime is when the server ingested the packet, so it is identical for
  // every member of a batched transmission (CS-* v5 sends up to five
  // measurements taken a minute apart in ONE packet). Keying a chart on it makes
  // four of the five values overwrite each other; sensortime carries the real,
  // staggered measurement time.
  //
  // sensortime is only trustworthy near the ingestion time: LoRa sensors without
  // a clock put their millis() into that field, which arrives as a timestamp in
  // January 1970 (or, after weeks of uptime, far in the future).
  //
  // Collection-address telegrams (M-Bus readouts) never use sensortime: the
  // field carries the METER's own clock, which can be wrong-but-plausible for
  // weeks (FRANK incident 08/2026) — a readout is measured the moment it is
  // read, and collections never batch.
  //
  // Staleness checks deliberately keep using recordedtime — "when did we last
  // hear from this sensor" really is about reception.
  //
  effectiveTime (dataPackage) {
    const recorded = dataPackage ? dataPackage.recordedtime : null
    const sensed = dataPackage ? dataPackage.sensortime : null
    if (!sensed || !recorded) {
      return recorded
    }
    if (dataPackage.collectionaddress) {
      return recorded
    }
    const lag = moment(String(recorded)).valueOf() - moment(String(sensed)).valueOf()
    if (lag < -PLAUSIBLE_SENSORTIME_FUTURE_SLACK_MS) {
      return recorded // sensortime in the future — broken clock
    }
    return lag <= PLAUSIBLE_SENSORTIME_WINDOW_MS ? sensed : recorded
  },

  //
  // Backfill chart points for the minutes covered by a CDC_NOCHANGE_COUNT
  // tuple. The suppressed minutes BEFORE the carrying package had the value
  // of the PREVIOUS package (the measurement inside the carrying package is
  // the new value). Writes into dataObj ('H:mm' keys, same convention as
  // firstGlanceData) and returns the number of points written.
  //
  // noChangeCount  numeric value of the vt:36 tuple
  // prevMeasurement / prevStamp  value + moment of the previous package in
  //                              this series (null on the first package —
  //                              then nothing is filled)
  // stamp  moment of the carrying package
  //
  backfillNoChange (dataObj, noChangeCount, prevMeasurement, prevStamp, stamp) {
    if (!dataObj || !noChangeCount || noChangeCount <= 0) return 0
    if (prevMeasurement == null || prevStamp == null || stamp == null) return 0
    // never overwrite the real previous point: cap the fill at the gap size
    const gapMinutes = moment(stamp).diff(moment(prevStamp), 'minutes') - 1
    const effective = Math.min(Math.floor(noChangeCount), gapMinutes)
    for (let i = 1; i <= effective; i++) {
      dataObj[moment(stamp).subtract(i, 'minutes').format('H:mm')] = prevMeasurement
    }
    return Math.max(effective, 0)
  },

  //
  // Determines the max-silence window (minutes) of a sensor, or null when
  // the sensor is not known to be send-on-change. Priority:
  //   1. explicit override `maxSilenceMin` in the sensor's raw json
  //      (authoritative — set during rollout of v5 sensors)
  //   2. a vt:36 tuple among the latest values => firmware default
  //   3. sticky flag (vt:36 was observed earlier in this session)
  //
  maxSilenceMinutes (jsonString, latestValues, stickySeen) {
    if (jsonString) {
      try {
        const parsed = JSON.parse(jsonString)
        const override = Number(parsed && parsed.maxSilenceMin)
        if (Number.isFinite(override) && override > 0) {
          return override
        }
      } catch (e) {
        // malformed sensor json — fall through to auto detection
      }
    }
    if (Array.isArray(latestValues) && latestValues.some(v => v && v.type === NOCHANGE_VALUETYPE)) {
      return DEFAULT_MAX_SILENCE_MINUTES
    }
    if (stickySeen) {
      return DEFAULT_MAX_SILENCE_MINUTES
    }
    return null
  },

  //
  // Two-point series carrying the last confirmed value forward to "now"
  // (unconfirmed prediction, rendered dashed). Returns {} when there is
  // nothing to predict (no anchor, or the anchor is fresher than a minute).
  //
  buildPrediction (lastStamp, lastMeasurement, now) {
    if (lastStamp == null || lastMeasurement == null) return {}
    const from = moment(lastStamp)
    const until = moment(now)
    if (until.diff(from, 'minutes') < 1) return {}
    const series = {}
    series[from.format('H:mm')] = lastMeasurement
    series[until.format('H:mm')] = lastMeasurement
    return series
  },

  //
  // Union of the keys of several chart-data objects, sorted chronologically.
  // 'H:mm' keys must not be sorted as strings ('10:00' < '9:00' would fail).
  //
  unionSortedKeys (seriesArray) {
    const keys = new Set()
    for (const series of seriesArray || []) {
      for (const key of Object.keys(series || {})) {
        keys.add(key)
      }
    }
    return Array.from(keys).sort((a, b) =>
      moment(a, 'H:mm').valueOf() - moment(b, 'H:mm').valueOf())
  }
}
